/**
 * CameraHttpSettingsSheet — operator-facing HTTP config push UI.
 *
 * Renders only for cameras with ingestion_type='http'. PATCHes
 * /api/cameras/{id}/ with `desired_pir_interval`, `desired_photo_size`,
 * `desired_video_resolution`, `desired_video_length`, and
 * `desired_extra_settings`. Backend translates to firmware enum codes and
 * exposes them at GET /InfraredCamera/SetCode/imei/{IMEI}/; the camera
 * firmware polls that endpoint on its own schedule.
 *
 * Convergence model: on the first successful SetCode emission for a
 * desired key, treat the wire delivery as the application moment, mirror
 * the value into `Camera.<setting>`, and mark the audit row converged.
 * The DeterCam firmware does not post applied state back, so wire delivery
 * is the strongest available signal. The /api/params handler stays in code
 * as defensive insurance for any future firmware that does post back.
 */

import * as Dialog from '@radix-ui/react-dialog'
import { useState, useEffect, useMemo } from 'react'
import { X, Loader2, SlidersHorizontal, AlertTriangle } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { camerasApi } from '@/api/endpoints'
import type { Camera, CameraUpdateRequest, DesiredExtraSettings } from '@/types'

interface CameraHttpSettingsSheetProps {
  camera: Camera
  open: boolean
  onOpenChange: (open: boolean) => void
}

// ─────────────────────────────────────────────────────────────────────
// Camera setting enums (mirrored from backend services/boly_enums.py)
// ─────────────────────────────────────────────────────────────────────

const PIR_INTERVAL_OPTIONS: Array<{ value: number; label: string }> = [
  ...[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((n) => ({ value: n, label: `${n} sec` })),
  ...[1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map((m) => ({
    value: m * 60,
    label: `${m} min`,
  })),
]
// Photo / video / PIR-trigger option sets aligned to the Document C spec
// shipped with V1.04.06030-T firmware (2026-05-13). Previous BG662M
// (V1.04.06028) options ('12'/'14'/'36'/'48' MP, etc.) never actually
// applied because the legacy #E#…# settings parser was broken — kept in git
// history for reference but removed from the UI since they'd be encoded
// to no-op values by the menu_txt_encoder. See change proposal #042.
const PHOTO_SIZE_OPTIONS = [
  { value: '8M', label: '8 MP' },
  { value: '10M', label: '10 MP' },
  { value: '32M', label: '32 MP' },
  { value: '56M', label: '56 MP' },
]
const VIDEO_RESOLUTION_OPTIONS = [
  { value: '3840x2160', label: '3840×2160 (4K)' },
  { value: '2688x1520', label: '2688×1520 (2.7K)' },
  { value: '1920x1080', label: '1920×1080 (1080p)' },
  { value: '1280x720', label: '1280×720 (720p)' },
  { value: '640x480', label: '640×480 (VGA)' },
  { value: '320x240', label: '320×240 (QVGA)' },
]
const VIDEO_LENGTH_OPTIONS = [1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 90, 120].map((n) => ({
  value: n,
  label: `${n} sec`,
}))
const PIR_TRIGGER_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
]
const CAMERA_MODE_OPTIONS = [
  { value: 'photo', label: 'Photo' },
  { value: 'video', label: 'Video' },
  { value: 'both', label: 'Photo + Video' },
]
const ATTACHMENT_SIZE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
]
const TIME_LAPSE_MIN_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
const TIME_LAPSE_HR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8]
const ON_OFF_OPTIONS = [
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
]
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// ── #044 Phase B: full Document C settings option sets ──
const DATE_TYPE_OPTIONS = [
  { value: 'ymd', label: 'Y-M-D' },
  { value: 'mdy', label: 'M-D-Y' },
  { value: 'dmy', label: 'D-M-Y' },
]
const AUTO_SIM_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'manual', label: 'Manual' },
]
const LOCK_NET_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: '2g', label: '2G' },
  { value: '3g', label: '3G' },
  { value: '4g', label: '4G' },
]
const ALARM_FLASH_OPTIONS = [
  ...Array.from({ length: 12 }, (_, i) => ({ value: `BR${i + 1}`, label: `Burst ${i + 1}` })),
  ...[2, 3, 4, 5].map((n) => ({ value: `BRDOUBLE${n}`, label: `Burst-double ${n}` })),
  ...Array.from({ length: 6 }, (_, i) => ({ value: `WHITE${i + 1}`, label: `White ${i + 1}` })),
]
const LANGUAGE_OPTIONS = [
  'ENGLISH', 'FINLAND', 'GERMANY', 'FRENCH', 'SPAIN', 'SWEDISH', 'FINNISH',
  'NORWAY', 'DANISH', 'ITALIAN', 'POLISH', 'RUSSIAN', 'JAPANESE',
].map((v) => ({ value: v, label: v[0] + v.slice(1).toLowerCase() }))
const HEARTBEAT_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'Every 6h' },
  { value: 2, label: 'Every 12h' },
  { value: 3, label: 'Every 24h' },
]
// Inclusive numeric ranges → dropdown options.
const intRange = (lo: number, hi: number) =>
  Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

const UNCHANGED = '__unchanged__' as const

type Tristate<T> = T | typeof UNCHANGED

function fieldChanged<T>(initial: T, current: T): boolean {
  return JSON.stringify(initial) !== JSON.stringify(current)
}

// ─────────────────────────────────────────────────────────────────────
// FieldRow — section row layout for one labelled control
// ─────────────────────────────────────────────────────────────────────

function FieldRow({
  label,
  hint,
  reported,
  children,
}: {
  label: string
  hint?: string
  reported?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-border/50 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        {reported && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Camera reports: <span className="font-mono">{reported}</span>
          </p>
        )}
      </div>
      <div className="shrink-0 min-w-[180px] max-w-[260px]">{children}</div>
    </div>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mt-2 mb-1">
      {children}
    </h3>
  )
}

function PendingBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-xs font-medium">
      {count} pending push
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────

export function CameraHttpSettingsSheet({ camera, open, onOpenChange }: CameraHttpSettingsSheetProps) {
  const queryClient = useQueryClient()

  // ── Bucket-1 form state (typed columns)
  const [pirInterval, setPirInterval] = useState<Tristate<number>>(UNCHANGED)
  const [photoSize, setPhotoSize] = useState<Tristate<string>>(UNCHANGED)
  const [videoResolution, setVideoResolution] = useState<Tristate<string>>(UNCHANGED)
  const [videoLength, setVideoLength] = useState<Tristate<number>>(UNCHANGED)

  // ── Bucket-2 form state (one slot per push-only setting; UNCHANGED means "no change")
  // NonNullable: send_mode? is optional so the raw type includes `undefined`,
  // which breaks discriminated-union narrowing on .mode/.count. State is only
  // ever UNCHANGED or a concrete {mode,...} object — never undefined.
  const [sendMode, setSendMode] = useState<NonNullable<DesiredExtraSettings['send_mode']> | typeof UNCHANGED>(UNCHANGED)
  const [workHourMode, setWorkHourMode] = useState<'unchanged' | 'always' | 'window'>('unchanged')
  const [workHourFrom, setWorkHourFrom] = useState<string>('08:00')
  const [workHourTo, setWorkHourTo] = useState<string>('18:00')
  const [pirTrigger, setPirTrigger] = useState<Tristate<DesiredExtraSettings['pir_trigger']>>(UNCHANGED)
  const [cameraMode, setCameraMode] = useState<Tristate<DesiredExtraSettings['camera_mode']>>(UNCHANGED)
  const [smsControl, setSmsControl] = useState<Tristate<'on' | 'off'>>(UNCHANGED)
  const [timeLapseUnit, setTimeLapseUnit] = useState<'unchanged' | 'off' | 'minutes' | 'hours'>('unchanged')
  const [timeLapseValue, setTimeLapseValue] = useState<number>(30)
  const [workDay, setWorkDay] = useState<boolean[] | null>(null)  // null = unchanged
  const [attachmentSize, setAttachmentSize] = useState<Tristate<DesiredExtraSettings['attachment_size']>>(UNCHANGED)
  const [gps, setGps] = useState<Tristate<'on' | 'off'>>(UNCHANGED)
  const [recycleState, setRecycleState] = useState<Tristate<'on' | 'off'>>(UNCHANGED)

  // ── #044 Phase B: remaining Document C settings
  const [photoBurst, setPhotoBurst] = useState<Tristate<number>>(UNCHANGED)
  const [dateType, setDateType] = useState<Tristate<DesiredExtraSettings['date_type']>>(UNCHANGED)
  const [autoSim, setAutoSim] = useState<Tristate<DesiredExtraSettings['auto_sim']>>(UNCHANGED)
  const [lockNet, setLockNet] = useState<Tristate<DesiredExtraSettings['lock_net']>>(UNCHANGED)
  const [alarmFlash, setAlarmFlash] = useState<Tristate<DesiredExtraSettings['alarm_flash']>>(UNCHANGED)
  const [language, setLanguage] = useState<Tristate<DesiredExtraSettings['language']>>(UNCHANGED)
  const [beepSound, setBeepSound] = useState<Tristate<'on' | 'off'>>(UNCHANGED)
  const [timeStamp, setTimeStamp] = useState<Tristate<'on' | 'off'>>(UNCHANGED)
  const [alarmByAi, setAlarmByAi] = useState<Tristate<number>>(UNCHANGED)
  const [alarmByMotion, setAlarmByMotion] = useState<Tristate<'on' | 'off'>>(UNCHANGED)
  const [alarmByCloud, setAlarmByCloud] = useState<Tristate<'on' | 'off'>>(UNCHANGED)
  const [alarmSound, setAlarmSound] = useState<Tristate<number>>(UNCHANGED)
  const [alarmLight, setAlarmLight] = useState<Tristate<number>>(UNCHANGED)
  const [alarmSoundVolume, setAlarmSoundVolume] = useState<Tristate<number>>(UNCHANGED)
  const [ukAttachSize, setUkAttachSize] = useState<Tristate<number>>(UNCHANGED)
  const [ukLockMenu, setUkLockMenu] = useState<Tristate<number>>(UNCHANGED)
  const [heartbeat, setHeartbeat] = useState<Tristate<number>>(UNCHANGED)
  const [smsTimeDelay, setSmsTimeDelay] = useState<Tristate<number>>(UNCHANGED)
  const [timeZone, setTimeZone] = useState<Tristate<number>>(UNCHANGED)
  const [timeZoneOffset, setTimeZoneOffset] = useState<Tristate<number>>(UNCHANGED)

  // Snapshot of "what was loaded" for diff-on-save logic
  const initialState = useMemo(() => {
    const ex = camera.desired_extra_settings || {}
    return {
      pirInterval: camera.desired_pir_interval ?? UNCHANGED,
      photoSize: camera.desired_photo_size || UNCHANGED,
      videoResolution: camera.desired_video_resolution || UNCHANGED,
      videoLength: camera.desired_video_length ?? UNCHANGED,
      extras: ex,
    }
  }, [camera])

  // Reset on open / camera change.
  useEffect(() => {
    if (!open) return
    const ex = camera.desired_extra_settings || {}
    setPirInterval(camera.desired_pir_interval ?? UNCHANGED)
    setPhotoSize(camera.desired_photo_size || UNCHANGED)
    setVideoResolution(camera.desired_video_resolution || UNCHANGED)
    setVideoLength(camera.desired_video_length ?? UNCHANGED)
    setSendMode(ex.send_mode ?? UNCHANGED)
    if (ex.work_hour) {
      if (ex.work_hour === 'off') {
        setWorkHourMode('always')
      } else {
        setWorkHourMode('window')
        const [from, to] = ex.work_hour.split('-')
        setWorkHourFrom(from)
        setWorkHourTo(to)
      }
    } else {
      setWorkHourMode('unchanged')
    }
    setPirTrigger(ex.pir_trigger ?? UNCHANGED)
    setCameraMode(ex.camera_mode ?? UNCHANGED)
    setSmsControl(ex.sms_control ?? UNCHANGED)
    if (ex.time_lapse) {
      setTimeLapseUnit(ex.time_lapse.unit)
      if (ex.time_lapse.unit !== 'off') setTimeLapseValue(ex.time_lapse.value)
    } else {
      setTimeLapseUnit('unchanged')
    }
    setWorkDay(ex.work_day ? [...ex.work_day].map((c) => c === '1') : null)
    setAttachmentSize(ex.attachment_size ?? UNCHANGED)
    setGps(ex.gps ?? UNCHANGED)
    setRecycleState(ex.recycle_storage_state ?? UNCHANGED)
    setPhotoBurst(ex.photo_burst ?? UNCHANGED)
    setDateType(ex.date_type ?? UNCHANGED)
    setAutoSim(ex.auto_sim ?? UNCHANGED)
    setLockNet(ex.lock_net ?? UNCHANGED)
    setAlarmFlash(ex.alarm_flash ?? UNCHANGED)
    setLanguage(ex.language ?? UNCHANGED)
    setBeepSound(ex.beep_sound ?? UNCHANGED)
    setTimeStamp(ex.time_stamp ?? UNCHANGED)
    setAlarmByAi(ex.alarm_by_ai ?? UNCHANGED)
    setAlarmByMotion(ex.alarm_by_motion ?? UNCHANGED)
    setAlarmByCloud(ex.alarm_by_cloud ?? UNCHANGED)
    setAlarmSound(ex.alarm_sound ?? UNCHANGED)
    setAlarmLight(ex.alarm_light ?? UNCHANGED)
    setAlarmSoundVolume(ex.alarm_sound_volume ?? UNCHANGED)
    setUkAttachSize(ex.uk_attach_size ?? UNCHANGED)
    setUkLockMenu(ex.uk_lock_menu ?? UNCHANGED)
    setHeartbeat(ex.heartbeat ?? UNCHANGED)
    setSmsTimeDelay(ex.sms_time_delay ?? UNCHANGED)
    setTimeZone(ex.time_zone ?? UNCHANGED)
    setTimeZoneOffset(ex.time_zone_offset ?? UNCHANGED)
  }, [open, camera])

  // ── Build the PATCH payload from current form state vs initial.
  const buildPayload = (): CameraUpdateRequest => {
    const payload: CameraUpdateRequest = {}

    // Bucket-1 typed columns
    if (pirInterval !== initialState.pirInterval) {
      payload.desired_pir_interval = pirInterval === UNCHANGED ? null : pirInterval
    }
    if (photoSize !== initialState.photoSize) {
      payload.desired_photo_size = photoSize === UNCHANGED ? '' : photoSize
    }
    if (videoResolution !== initialState.videoResolution) {
      payload.desired_video_resolution = videoResolution === UNCHANGED ? '' : videoResolution
    }
    if (videoLength !== initialState.videoLength) {
      payload.desired_video_length = videoLength === UNCHANGED ? null : videoLength
    }

    // Build the bucket-2 dict (full replacement on PATCH).
    const extras: DesiredExtraSettings = {}
    if (sendMode !== UNCHANGED && sendMode) extras.send_mode = sendMode
    if (workHourMode === 'always') extras.work_hour = 'off'
    else if (workHourMode === 'window') extras.work_hour = `${workHourFrom}-${workHourTo}`
    if (pirTrigger !== UNCHANGED && pirTrigger) extras.pir_trigger = pirTrigger
    if (cameraMode !== UNCHANGED && cameraMode) extras.camera_mode = cameraMode
    if (smsControl !== UNCHANGED && smsControl) extras.sms_control = smsControl
    if (timeLapseUnit !== 'unchanged') {
      if (timeLapseUnit === 'off') extras.time_lapse = { unit: 'off' }
      else if (timeLapseUnit === 'minutes') extras.time_lapse = { unit: 'minutes', value: timeLapseValue }
      else extras.time_lapse = { unit: 'hours', value: timeLapseValue }
    }
    if (workDay) extras.work_day = workDay.map((b) => (b ? '1' : '0')).join('')
    if (attachmentSize !== UNCHANGED && attachmentSize) extras.attachment_size = attachmentSize
    if (gps !== UNCHANGED && gps) extras.gps = gps
    if (recycleState !== UNCHANGED && recycleState) extras.recycle_storage_state = recycleState
    // #044 Phase B
    if (photoBurst !== UNCHANGED) extras.photo_burst = photoBurst as 1 | 2 | 3
    if (dateType !== UNCHANGED && dateType) extras.date_type = dateType
    if (autoSim !== UNCHANGED && autoSim) extras.auto_sim = autoSim
    if (lockNet !== UNCHANGED && lockNet) extras.lock_net = lockNet
    if (alarmFlash !== UNCHANGED && alarmFlash) extras.alarm_flash = alarmFlash
    if (language !== UNCHANGED && language) extras.language = language
    if (beepSound !== UNCHANGED && beepSound) extras.beep_sound = beepSound
    if (timeStamp !== UNCHANGED && timeStamp) extras.time_stamp = timeStamp
    if (alarmByAi !== UNCHANGED) extras.alarm_by_ai = alarmByAi as number
    if (alarmByMotion !== UNCHANGED && alarmByMotion) extras.alarm_by_motion = alarmByMotion
    if (alarmByCloud !== UNCHANGED && alarmByCloud) extras.alarm_by_cloud = alarmByCloud
    if (alarmSound !== UNCHANGED) extras.alarm_sound = alarmSound as number
    if (alarmLight !== UNCHANGED) extras.alarm_light = alarmLight as number
    if (alarmSoundVolume !== UNCHANGED) extras.alarm_sound_volume = alarmSoundVolume as 0 | 1 | 2
    if (ukAttachSize !== UNCHANGED) extras.uk_attach_size = ukAttachSize as number
    if (ukLockMenu !== UNCHANGED) extras.uk_lock_menu = ukLockMenu as 0 | 1
    if (heartbeat !== UNCHANGED) extras.heartbeat = heartbeat as 0 | 1 | 2 | 3
    if (smsTimeDelay !== UNCHANGED) extras.sms_time_delay = smsTimeDelay as 0 | 1 | 2 | 3 | 4
    if (timeZone !== UNCHANGED) extras.time_zone = timeZone as number
    if (timeZoneOffset !== UNCHANGED) extras.time_zone_offset = timeZoneOffset as number

    if (fieldChanged(initialState.extras, extras)) payload.desired_extra_settings = extras
    return payload
  }

  // ── PATCH mutation
  const updateMutation = useMutation({
    mutationFn: (payload: CameraUpdateRequest) => camerasApi.update(camera.id, payload),
    onSuccess: () => {
      toast.success('Settings queued for next camera check-in')
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
      const msg = data
        ? Object.entries(data).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join('; ')
        : 'Save failed'
      toast.error(msg)
    },
  })

  const handleSave = () => {
    const payload = buildPayload()
    if (Object.keys(payload).length === 0) {
      toast.message('No changes to save')
      return
    }
    updateMutation.mutate(payload)
  }

  const isSaving = updateMutation.isPending

  // Narrowed once here so the count Input's onChange closure is type-safe
  // (TS doesn't preserve discriminated-union narrowing into closures).
  const sendModeObj =
    sendMode !== UNCHANGED && sendMode.mode !== 'off' ? sendMode : null

  // #052 — the count now comes from the backend (camera.settings_pending_count,
  // same pending_menu_delta source as settings_state) so it can't contradict
  // the applied/pending badge. The old local memo counted "settings
  // configured", which falsely showed e.g. "7 pending push" for an applied
  // camera.

  return (
    <>
    <Dialog.Root open={open} onOpenChange={isSaving ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-full max-w-2xl border-l border-border bg-background shadow-elevation-3 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-300">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <Dialog.Title className="text-base font-semibold truncate">HTTP Camera Settings</Dialog.Title>
                <p className="text-xs text-muted-foreground truncate">{camera.name} · {camera.imei}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {camera.settings_state === 'applied' && (
                <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">Settings applied</span>
              )}
              {camera.settings_state === 'pending' && (
                <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20">Settings pending</span>
              )}
              <PendingBadge count={camera.settings_pending_count} />
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" disabled={isSaving}>
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>
          </div>

          {/* Status banner — explains the delivery + confirmation model. */}
          <div className="px-5 py-3 bg-muted/40 border-b border-border shrink-0 text-xs text-muted-foreground flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <p>
              Changes are delivered to the camera on its next settings poll. The DeterCam firmware does not confirm applied state, so settings are reported as "delivered" once they reach the camera. Applied state cannot be independently verified server-side.
            </p>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">

            {/* ─── Capture (bucket-1) ─── */}
            <section>
              <SectionHeading>Capture</SectionHeading>
              <FieldRow label="PIR interval" hint="Wait between motion triggers" reported={camera.pir_interval !== null ? `${camera.pir_interval}s` : undefined}>
                <Select
                  value={pirInterval === UNCHANGED ? UNCHANGED : String(pirInterval)}
                  onValueChange={(v) => setPirInterval(v === UNCHANGED ? UNCHANGED : Number(v))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {PIR_INTERVAL_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Photo size" reported={camera.photo_size ? `${camera.photo_size} MP` : undefined}>
                <Select value={photoSize === UNCHANGED ? UNCHANGED : photoSize} onValueChange={(v) => setPhotoSize(v === UNCHANGED ? UNCHANGED : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {PHOTO_SIZE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Video resolution" reported={camera.video_resolution || undefined}>
                <Select value={videoResolution === UNCHANGED ? UNCHANGED : videoResolution} onValueChange={(v) => setVideoResolution(v === UNCHANGED ? UNCHANGED : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {VIDEO_RESOLUTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Video length" reported={camera.video_length !== null ? `${camera.video_length}s` : undefined}>
                <Select value={videoLength === UNCHANGED ? UNCHANGED : String(videoLength)} onValueChange={(v) => setVideoLength(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {VIDEO_LENGTH_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Camera mode">
                <Select value={cameraMode === UNCHANGED ? UNCHANGED : cameraMode!} onValueChange={(v) => setCameraMode(v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['camera_mode']))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {CAMERA_MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

            </section>

            {/* ─── Trigger ─── */}
            <section>
              <SectionHeading>Trigger</SectionHeading>
              <FieldRow label="PIR sensitivity">
                <Select value={pirTrigger === UNCHANGED ? UNCHANGED : pirTrigger!} onValueChange={(v) => setPirTrigger(v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['pir_trigger']))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {PIR_TRIGGER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Time lapse" hint="Take photo at fixed interval regardless of motion">
                <div className="flex gap-2">
                  <Select
                    value={timeLapseUnit}
                    onValueChange={(v) => setTimeLapseUnit(v as typeof timeLapseUnit)}
                  >
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unchanged">(no change)</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="minutes">Every N min</SelectItem>
                      <SelectItem value="hours">Every N hr</SelectItem>
                    </SelectContent>
                  </Select>
                  {(timeLapseUnit === 'minutes' || timeLapseUnit === 'hours') && (
                    <Select value={String(timeLapseValue)} onValueChange={(v) => setTimeLapseValue(Number(v))}>
                      <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(timeLapseUnit === 'minutes' ? TIME_LAPSE_MIN_OPTIONS : TIME_LAPSE_HR_OPTIONS).map((n) => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </FieldRow>
            </section>

            {/* ─── Schedule ─── */}
            <section>
              <SectionHeading>Schedule</SectionHeading>
              <FieldRow label="Work hour" hint="When the camera is active each day">
                <div className="flex flex-col gap-2">
                  <Select value={workHourMode} onValueChange={(v) => setWorkHourMode(v as typeof workHourMode)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unchanged">(no change)</SelectItem>
                      <SelectItem value="always">Always on</SelectItem>
                      <SelectItem value="window">Time window</SelectItem>
                    </SelectContent>
                  </Select>
                  {workHourMode === 'window' && (
                    <div className="flex gap-1.5 items-center">
                      <Input type="time" value={workHourFrom} onChange={(e) => setWorkHourFrom(e.target.value)} className="h-8" />
                      <span className="text-xs text-muted-foreground">to</span>
                      <Input type="time" value={workHourTo} onChange={(e) => setWorkHourTo(e.target.value)} className="h-8" />
                    </div>
                  )}
                </div>
              </FieldRow>

              <FieldRow label="Work days" hint="Sunday-first: leftmost bit is Sunday.">
                <div className="flex gap-1 flex-wrap justify-end">
                  {DAY_LABELS.map((lbl, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        if (workDay === null) {
                          const arr = [false, false, false, false, false, false, false]
                          arr[i] = true
                          setWorkDay(arr)
                        } else {
                          const arr = [...workDay]
                          arr[i] = !arr[i]
                          setWorkDay(arr)
                        }
                      }}
                      className={`h-7 w-9 rounded text-xs font-medium border ${
                        workDay && workDay[i] ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-muted-foreground'
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                  {workDay !== null && (
                    <button type="button" onClick={() => setWorkDay(null)} className="h-7 px-2 rounded text-xs border border-border text-muted-foreground">
                      Clear
                    </button>
                  )}
                </div>
              </FieldRow>
            </section>

            {/* ─── Storage ─── */}
            <section>
              <SectionHeading>Storage</SectionHeading>
              <FieldRow label="Attachment size" hint="JPEG quality — applies to photo upload">
                <Select value={attachmentSize === UNCHANGED ? UNCHANGED : attachmentSize!} onValueChange={(v) => setAttachmentSize(v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['attachment_size']))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ATTACHMENT_SIZE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Recycle storage" hint="Overwrite oldest media when SD fills">
                <Select value={recycleState === UNCHANGED ? UNCHANGED : recycleState!} onValueChange={(v) => setRecycleState(v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

            </section>

            {/* ─── Network ─── */}
            <section>
              <SectionHeading>Network</SectionHeading>
              <FieldRow label="Send mode" hint="Instant or daily batch; off disables uploads">
                <Select
                  value={sendMode === UNCHANGED ? UNCHANGED : sendMode.mode}
                  onValueChange={(v) => {
                    if (v === UNCHANGED) setSendMode(UNCHANGED)
                    else if (v === 'off') setSendMode({ mode: 'off' })
                    else setSendMode({ mode: v as 'instant' | 'daily', count: 100 })
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    <SelectItem value="instant">Instant</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="off">Off</SelectItem>
                  </SelectContent>
                </Select>
                {sendModeObj && (
                  <Input
                    type="number" min={1} max={100}
                    value={sendModeObj.count}
                    onChange={(e) =>
                      setSendMode({ mode: sendModeObj.mode, count: Number(e.target.value) })
                    }
                    className="h-8 mt-1.5"
                    placeholder="Send max num (1-100)"
                  />
                )}
              </FieldRow>

              <FieldRow label="SMS control" hint="GSM modem stays on to accept SMS commands">
                <Select value={smsControl === UNCHANGED ? UNCHANGED : smsControl!} onValueChange={(v) => setSmsControl(v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="GPS" hint="AutoGPS — camera acquires location on capture">
                <Select value={gps === UNCHANGED ? UNCHANGED : gps!} onValueChange={(v) => setGps(v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Auto SIM" hint="Automatic vs manual SIM selection">
                <Select value={autoSim === UNCHANGED ? UNCHANGED : autoSim!} onValueChange={(v) => setAutoSim(v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['auto_sim']))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {AUTO_SIM_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Network lock" hint="Force a cellular generation">
                <Select value={lockNet === UNCHANGED ? UNCHANGED : lockNet!} onValueChange={(v) => setLockNet(v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['lock_net']))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {LOCK_NET_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Heartbeat" hint="Status check-in interval">
                <Select value={heartbeat === UNCHANGED ? UNCHANGED : String(heartbeat)} onValueChange={(v) => setHeartbeat(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {HEARTBEAT_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="SMS time delay" hint="0-4">
                <Select value={smsTimeDelay === UNCHANGED ? UNCHANGED : String(smsTimeDelay)} onValueChange={(v) => setSmsTimeDelay(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(0, 4).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
            </section>

            {/* ─── Deterrent ─── */}
            <section>
              <SectionHeading>Deterrent</SectionHeading>
              <FieldRow label="Alarm by motion">
                <Select value={alarmByMotion === UNCHANGED ? UNCHANGED : alarmByMotion!} onValueChange={(v) => setAlarmByMotion(v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Alarm by cloud">
                <Select value={alarmByCloud === UNCHANGED ? UNCHANGED : alarmByCloud!} onValueChange={(v) => setAlarmByCloud(v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Alarm by AI" hint="0 = off, 1-10 = alarm seconds">
                <Select value={alarmByAi === UNCHANGED ? UNCHANGED : String(alarmByAi)} onValueChange={(v) => setAlarmByAi(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(0, 10).map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? 'Off' : `${n}s`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Alarm sound" hint="0-20 seconds">
                <Select value={alarmSound === UNCHANGED ? UNCHANGED : String(alarmSound)} onValueChange={(v) => setAlarmSound(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(0, 20).map((n) => <SelectItem key={n} value={String(n)}>{n}s</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Alarm light" hint="0-20 seconds">
                <Select value={alarmLight === UNCHANGED ? UNCHANGED : String(alarmLight)} onValueChange={(v) => setAlarmLight(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(0, 20).map((n) => <SelectItem key={n} value={String(n)}>{n}s</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Alarm sound volume" hint="0-2">
                <Select value={alarmSoundVolume === UNCHANGED ? UNCHANGED : String(alarmSoundVolume)} onValueChange={(v) => setAlarmSoundVolume(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(0, 2).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Alarm flash" hint="Deterrent flash pattern">
                <Select value={alarmFlash === UNCHANGED ? UNCHANGED : alarmFlash!} onValueChange={(v) => setAlarmFlash(v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['alarm_flash']))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ALARM_FLASH_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
            </section>

            {/* ─── Regional ─── */}
            <section>
              <SectionHeading>Regional</SectionHeading>
              <FieldRow label="Language">
                <Select value={language === UNCHANGED ? UNCHANGED : language!} onValueChange={(v) => setLanguage(v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['language']))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {LANGUAGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Date format">
                <Select value={dateType === UNCHANGED ? UNCHANGED : dateType!} onValueChange={(v) => setDateType(v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['date_type']))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {DATE_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Time zone" hint="Hours from UTC (-12..12)">
                <Select value={timeZone === UNCHANGED ? UNCHANGED : String(timeZone)} onValueChange={(v) => setTimeZone(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(-12, 12).map((n) => <SelectItem key={n} value={String(n)}>{n > 0 ? `+${n}` : n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Time zone offset" hint="Extra minutes (-60..60)">
                <Select value={timeZoneOffset === UNCHANGED ? UNCHANGED : String(timeZoneOffset)} onValueChange={(v) => setTimeZoneOffset(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(-60, 60).map((n) => <SelectItem key={n} value={String(n)}>{n > 0 ? `+${n}` : n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
            </section>

            {/* ─── System ─── */}
            <section>
              <SectionHeading>System</SectionHeading>
              <FieldRow label="Photo burst" hint="Photos per PIR trigger">
                <Select value={photoBurst === UNCHANGED ? UNCHANGED : String(photoBurst)} onValueChange={(v) => setPhotoBurst(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(1, 3).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Beep sound">
                <Select value={beepSound === UNCHANGED ? UNCHANGED : beepSound!} onValueChange={(v) => setBeepSound(v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="Timestamp on media">
                <Select value={timeStamp === UNCHANGED ? UNCHANGED : timeStamp!} onValueChange={(v) => setTimeStamp(v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="UK attach size" hint="0-9 (UK-region build)">
                <Select value={ukAttachSize === UNCHANGED ? UNCHANGED : String(ukAttachSize)} onValueChange={(v) => setUkAttachSize(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(0, 9).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>

              <FieldRow label="UK lock menu" hint="0 = off, 1 = lock camera menu">
                <Select value={ukLockMenu === UNCHANGED ? UNCHANGED : String(ukLockMenu)} onValueChange={(v) => setUkLockMenu(v === UNCHANGED ? UNCHANGED : Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCHANGED}>(no change)</SelectItem>
                    {intRange(0, 1).map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? 'Off' : 'Lock'}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
            </section>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
            <Dialog.Close asChild>
              <Button variant="outline" disabled={isSaving}>Cancel</Button>
            </Dialog.Close>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Save changes
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
    </>
  )
}
